import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

// Static fallback images
import heroImage1 from "@/assets/hero-wedding-1.jpg";
import heroImage2 from "@/assets/hero-wedding-2.jpg";
import heroImage3 from "@/assets/hero-wedding-3.jpg";

const fallbackSlides = [
  { image: heroImage1, alt: "Romantic wedding couple in garden" },
  { image: heroImage2, alt: "First dance under string lights" },
  { image: heroImage3, alt: "Beach wedding at sunset" },
];

interface HeroSlide {
  image: string;
  alt: string;
}

export function HeroCarousel() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [heroSlides, setHeroSlides] = useState<HeroSlide[]>(fallbackSlides);
  const [isLoading, setIsLoading] = useState(true);
  const [minPrice, setMinPrice] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch hero media
        const { data: locations, error: locError } = await supabase
          .from("portfolio_media_locations")
          .select("media_id")
          .eq("location", "homepage_hero");

        if (locError) throw locError;

        if (locations && locations.length > 0) {
          const mediaIds = locations.map((l) => l.media_id);
          
          const { data: mediaData, error: mediaError } = await supabase
            .from("portfolio_media")
            .select("*")
            .in("id", mediaIds)
            .eq("is_active", true)
            .eq("media_type", "image")
            .order("sort_order", { ascending: true });

          if (mediaError) throw mediaError;

          if (mediaData && mediaData.length > 0) {
            setHeroSlides(
              mediaData.map((m) => ({
                image: m.url,
                alt: m.alt_text || m.title,
              }))
            );
          }
        }

        // Fetch minimum package price
        const { data: priceData } = await supabase
          .from("pricing_packages")
          .select("price")
          .eq("is_active", true)
          .order("price", { ascending: true })
          .limit(1)
          .maybeSingle();

        if (priceData) {
          setMinPrice(priceData.price);
        }
      } catch (error) {
        console.error("Error fetching hero data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, [heroSlides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  }, [heroSlides.length]);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  // Reset current slide if slides change
  useEffect(() => {
    setCurrentSlide(0);
  }, [heroSlides]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <section className="relative h-[90vh] min-h-[600px] w-full overflow-hidden">
      {/* Background Images */}
      <AnimatePresence initial={false}>
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="absolute inset-0"
        >
          <img
            src={heroSlides[currentSlide].image}
            alt={heroSlides[currentSlide].alt}
            className="h-full w-full object-cover"
            fetchPriority={currentSlide === 0 ? "high" : "auto"}
            loading={currentSlide === 0 ? "eager" : "lazy"}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground/20 via-foreground/30 to-foreground/60" />
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <div className="relative z-10 flex h-full items-center">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="max-w-3xl"
          >
            <h1 className="font-serif text-4xl font-semibold leading-tight text-primary-foreground md:text-5xl lg:text-6xl">
              Beautiful wedding photography{" "}
              <span className="text-primary">without the luxury price tag</span>
            </h1>
            <p className="mt-6 text-lg text-primary-foreground/90 md:text-xl">
              Affordable photo + video + booth experiences for real couples and real budgets
            </p>
            
            {/* Price Anchor */}
            {minPrice && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="mt-4 text-lg font-medium text-primary"
              >
                Packages starting at {formatPrice(minPrice)}
              </motion.p>
            )}

            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <Button variant="hero" size="xl" asChild>
                <Link to="/contact">Check Availability</Link>
              </Button>
              <Button variant="hero-outline" size="xl" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-foreground" asChild>
                <Link to="/combo-packages">View Packages</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 z-20 -translate-y-1/2 rounded-full bg-background/20 p-3 text-primary-foreground backdrop-blur-sm transition-all hover:bg-background/40"
        aria-label="Previous slide"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 z-20 -translate-y-1/2 rounded-full bg-background/20 p-3 text-primary-foreground backdrop-blur-sm transition-all hover:bg-background/40"
        aria-label="Next slide"
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 z-20 flex -translate-x-1/2 gap-2">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`h-2 rounded-full transition-all ${
              index === currentSlide
                ? "w-8 bg-primary"
                : "w-2 bg-primary-foreground/50 hover:bg-primary-foreground/80"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
}